import React from "react";
import {
  XAxis,
  YAxis,
  Area,
  Line,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  Label,
  TooltipProps,
} from "recharts";
import {
  BusinessTrendChartData,
  TimeFrame,
} from "../../services/interfaces/IBusinessCostTrend";
import {
  dateFormatterToMMMYY,
  dateFormatterToMMDD,
  numberFormatter,
  truncateTo2Decimals,
} from "../../utils/formatter";

interface Intersection {
  x: number;
  y: number;
  line1isHigher: boolean;
  line1isHigherNext: boolean;
}

interface BusinessCostCompChartProps {
  data: BusinessTrendChartData[];
  id: string;
  timeFrame: TimeFrame;
  commodity: string | null;
  baseSKU: string | null;
}

const getIntersectionColor = (intersection: Intersection, isLast = false) => {
  if (isLast) {
    return intersection.line1isHigherNext ? "#86BC2599" : "#F4606099";
  }
  return intersection.line1isHigher ? "#86BC2599" : "#F4606099";
};

export const BusinessCostCompChart: React.FC<BusinessCostCompChartProps> = ({
  data,
  id,
  timeFrame,
  commodity,
  baseSKU,
}) => {
  const dataWithRange = data.map((d) => ({
    ...d,
    range: [d.business_cost, d.po_cost],
  }));

  const intersections: Intersection[] = data
    .map((d, i) =>
      intersect(
        i,
        d.business_cost,
        i + 1,
        data[i + 1]?.business_cost,
        i,
        d.po_cost,
        i + 1,
        data[i + 1]?.po_cost,
      ),
    )
    .filter((d): d is Intersection => d !== false && !isNaN(d.x));

  const filteredIntersections = intersections.filter(
    (d, i) => i === intersections.length - 1 || d.x !== intersections[i - 1]?.x,
  );

  const values = data.flatMap((d) => [
    d.business_cost,
    d.po_cost,
    d.sell_price,
  ]);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);

  const lowerBound = minValue * 0.99;
  const upperBound = maxValue * 1.01;

  const BusinessCostTrendTooltip: React.FC<TooltipProps<string, number>> = ({
    active,
    payload,
    label,
  }) => {
    const formatCurrencyValue = (value: any) => {
      const numericValue = Number(value);
      if (value !== null && value !== "" && !Number.isNaN(numericValue)) {
        return `$${numericValue.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      }
      return value;
    };

    if (active && payload && payload.length) {
      const filterPayload = payload.filter((item) => item.dataKey !== "range");
      const tooltipOrder: { dataKey: string; label: string }[] = [
        { dataKey: "sell_price", label: "Sales Price" },
        { dataKey: "business_cost", label: "Board Cost" },
        { dataKey: "po_cost", label: "PO Cost" },
      ];
      return (
        <div
          style={{
            backgroundColor: "#fff",
            padding: "10px",
            border: "1px solid #bebebe",
          }}
        >
          <p>
            Date:{" "}
            {timeFrame === TimeFrame.MONTH
              ? dateFormatterToMMMYY(label)
              : dateFormatterToMMDD(label)}
          </p>
          {tooltipOrder.map((orderItem) => {
            const item = filterPayload.find(
              (p) => p.dataKey === orderItem.dataKey,
            );
            if (!item) {
              return null;
            }
            return (
              <p
                key={orderItem.dataKey}
                style={{ color: item.color, textTransform: "capitalize" }}
              >
                {orderItem.label} : {formatCurrencyValue(item.value)}
              </p>
            );
          })}
        </div>
      );
    }

    return null;
  };

  const CustomYAxisLabel = ({ viewBox, value }: any) => {
    const { x, y, width, height } = viewBox;
    const lines = value.split("\n");
    const cx = x;
    const cy = height / 2;
    return (
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        transform={`rotate(-90, ${cx}, ${cy})`}
        fill="black"
        fontSize={"0.6vw"}
        fontWeight={600}
      >
        {lines.map((line: any, index: number) => (
          <tspan
            fill={index === 0 ? "#86BC25" : "black"}
            key={index}
            x={0}
            dy={`${index * 1.3}em`}
          >
            {line}
          </tspan>
        ))}
      </text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        margin={{
          top: 10,
          right: 20,
          bottom: 2,
          left: 20,
        }}
        data={dataWithRange}
      >
        <defs>
          <linearGradient id={id}>
            {filteredIntersections.length ? (
              filteredIntersections.map((intersection, i) => {
                const nextIntersection = filteredIntersections[i + 1];
                const isLast = i === filteredIntersections.length - 1;

                const closeColor = getIntersectionColor(intersection);
                const startColor = isLast
                  ? getIntersectionColor(intersection, true)
                  : getIntersectionColor(nextIntersection);

                const offset =
                  intersection.x /
                  (data.filter(
                    (d) =>
                      d.business_cost !== undefined && d.po_cost !== undefined,
                  ).length -
                    1);

                return (
                  <React.Fragment key={i}>
                    <stop
                      offset={offset}
                      stopColor={closeColor}
                      stopOpacity={0.9}
                    />
                    <stop
                      offset={offset}
                      stopColor={startColor}
                      stopOpacity={0.9}
                    />
                  </React.Fragment>
                );
              })
            ) : (
              <stop
                offset={0}
                stopColor={
                  data[0]?.business_cost > data[0]?.po_cost
                    ? "#86BC2599"
                    : "#F4606099"
                }
              />
            )}
          </linearGradient>
        </defs>

        <XAxis
          dataKey="date"
          tickLine={false}
          tickCount={0}
          strokeWidth={1}
          axisLine={false}
          hide
          padding={{ left: 20 }}
        />
        <YAxis
          // tickCount={7}
          tickLine={true}
          allowDecimals={false}
          allowDataOverflow={false}
          interval="preserveStartEnd"
          strokeWidth={1}
          // domain={[Math.floor(lowerBound), Math.ceil(upperBound)]}
          domain={([dataMin, dataMax]) => {
            const divFactor = dataMax > 100 ? 20 : dataMax > 1 ? 10 : 0.2;
            return [
              Math.floor(dataMin / divFactor) * divFactor,
              Math.ceil(dataMax / divFactor) * divFactor,
            ];
          }}
        >
          <Label
            className="text-fs12"
            style={{
              textAnchor: "middle",
              fill: "black",
            }}
            angle={-90}
            value={`${baseSKU}\n($/cft)`}
            content={<CustomYAxisLabel />}
            position={"insideLeft"}
          />
        </YAxis>
        <Tooltip content={<BusinessCostTrendTooltip />} />

        <Area
          dataKey="range"
          stroke="#8884d8"
          strokeWidth={0}
          fill={`url(#${id})`}
        />

        <Line
          type="linear"
          dataKey="po_cost"
          strokeWidth={2}
          stroke="#E49257"
          dot={{ stroke: "#E49257", strokeWidth: 2, r: 2, fill: "#E49257" }}
        />
        <Line
          type="linear"
          dataKey="business_cost"
          strokeWidth={2}
          stroke="#012169"
          dot={{ stroke: "#012169", strokeWidth: 2, r: 2, fill: "#012169" }}
        />
        <Line
          type="linear"
          dataKey="sell_price"
          strokeWidth={2}
          stroke="#0097A9"
          dot={{ stroke: "#0097A9", strokeWidth: 2, r: 2, fill: "#0097A9" }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

function intersect(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  x4: number,
  y4: number,
): Intersection | false {
  if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
    return false;
  }

  const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);

  if (denominator === 0) {
    return false;
  }

  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;

  if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
    return false;
  }

  const x = x1 + ua * (x2 - x1);
  const y = y1 + ua * (y2 - y1);

  const line1isHigher = y1 > y3;
  const line1isHigherNext = y2 > y4;

  return { x, y, line1isHigher, line1isHigherNext };
}
